import React, { useMemo, useState } from 'react';
import { Autocomplete, Box, Button, Checkbox, Chip, TextField, Typography } from '@mui/material';
import { Add as AddIcon, Inventory2Outlined as GroupIcon, LabelOutlined as ServiceIcon } from '@mui/icons-material';
import { priceOf, groupChoices, defaultPicked } from '../../utils/workOrderServiceLines';

const LISTBOX = { sx: { '& .MuiAutocomplete-option': { fontSize: '0.8125rem', py: 0.5, minHeight: 32 } } };
const aed = (n) => `AED ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Two-way switch styled like the dashboard tabs: a sunk track with the chosen side raised. */
const ModeSwitch = ({ mode, onChange }) => (
  <Box role="group" aria-label="Add by" sx={{ display: 'inline-flex', p: 0.375, borderRadius: 999, bgcolor: 'action.selected' }}>
    {[['service', 'Service', ServiceIcon], ['group', 'Group', GroupIcon]].map(([key, label, Icon]) => {
      const on = mode === key;
      return (
        <Box key={key} component="button" type="button" aria-pressed={on} onClick={() => onChange(key)}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, border: 0, cursor: 'pointer', font: 'inherit',
            fontSize: '0.75rem', fontWeight: 600, px: 1.25, py: 0.5, borderRadius: 999,
            bgcolor: on ? 'background.paper' : 'transparent', color: on ? 'text.primary' : 'text.secondary',
            boxShadow: on ? '0 1px 2px rgba(0,0,0,.12)' : 'none' }}>
          <Icon sx={{ fontSize: 14 }} aria-hidden />{label}
        </Box>
      );
    })}
  </Box>
);

/**
 * Picks what to add to an applicant: one service (with an optional VAT-inclusive markup) or a service
 * group. A group shows its services as a ticked list first — required ones ticked, optional ones not,
 * ones the client has no price for disabled — and adds only the ticked services.
 *
 * services       options for the single-service picker (already filtered to the current tab)
 * groupServices  this client's priced internal services, used to price a group's members
 * groups         service groups (Service Catalog → Service Groups); the switch hides when empty
 */
const ServicePicker = ({ services, groupServices, groups, onAddService, onAddGroup }) => {
  const activeGroups = useMemo(() => (groups || []).filter((g) => (g.status || 'ACTIVE') === 'ACTIVE' && (g.items || []).length), [groups]);
  const [mode, setMode] = useState('service');
  const [service, setService] = useState(null);
  const [markup, setMarkup] = useState('');
  const [group, setGroup] = useState(null);
  const [picked, setPicked] = useState(new Set());

  const showSwitch = activeGroups.length > 0;
  const inGroupMode = showSwitch && mode === 'group';
  const choices = useMemo(() => (group ? groupChoices(group, groupServices) : []), [group, groupServices]);
  const chosen = choices.filter((c) => c.available && picked.has(c.serviceId));
  const groupTotal = chosen.reduce((sum, c) => sum + priceOf(c.service), 0);

  const changeMode = (m) => { setMode(m); setService(null); setMarkup(''); setGroup(null); setPicked(new Set()); };
  const chooseGroup = (g) => { setGroup(g); setPicked(g ? defaultPicked(groupChoices(g, groupServices)) : new Set()); };
  const toggle = (id) => setPicked((prev) => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });

  const addService = () => { onAddService(service, markup); setService(null); setMarkup(''); };
  const addGroup = () => { onAddGroup(group, chosen.map((c) => c.service)); setGroup(null); setPicked(new Set()); };

  const preview = service ? priceOf(service) + (parseFloat(markup) || 0) : 0;

  return (
    <Box sx={{ display: 'grid', gap: 1.25 }}>
      {showSwitch && <ModeSwitch mode={mode} onChange={changeMode} />}

      {!inGroupMode && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.25 }}>
          <Autocomplete ListboxProps={LISTBOX} sx={{ flex: '1 1 260px', minWidth: 0 }} size="small"
            options={services || []} getOptionLabel={(o) => o.name || ''} value={service}
            onChange={(e, v) => setService(v)} isOptionEqualToValue={(a, b) => a.id === b.id}
            renderOption={(props, o) => (
              <Box component="li" {...props} key={o.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <span>{o.name}</span>
                <Typography component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>{aed(priceOf(o))}</Typography>
              </Box>
            )}
            renderInput={(params) => <TextField {...params} label="Select service" placeholder="Search services" />} />
          <TextField size="small" label="Markup, AED (optional)" type="number" value={markup} placeholder="0.00"
            onChange={(e) => setMarkup(e.target.value)} sx={{ flex: '0 1 190px' }}
            inputProps={{ min: 0, step: '0.01' }} helperText={null} />
          {service && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, ml: 'auto' }}>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: 'secondary.main', whiteSpace: 'nowrap' }}>{aed(preview)}</Typography>
              <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={addService} sx={{ px: 2 }}>Add</Button>
            </Box>
          )}
        </Box>
      )}

      {inGroupMode && (
        <>
          <Autocomplete ListboxProps={LISTBOX} size="small" options={activeGroups} value={group}
            getOptionLabel={(o) => o.name || ''} onChange={(e, v) => chooseGroup(v)} isOptionEqualToValue={(a, b) => a.id === b.id}
            renderOption={(props, o) => (
              <Box component="li" {...props} key={o.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <span>{o.name}</span>
                <Typography component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{`${o.items.length} services`}</Typography>
              </Box>
            )}
            renderInput={(params) => <TextField {...params} label="Select group" placeholder="Search groups" />} />
          {group && (
            <Box sx={{ borderRadius: 1.5, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
              <Box component="ul" aria-label="Services in this group" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                {choices.map((c) => {
                  const id = `grp-${group.id}-${c.serviceId}`;
                  return (
                    <Box component="li" key={c.serviceId} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.25,
                      borderBottom: '1px solid', borderColor: 'divider', opacity: c.available ? 1 : 0.55 }}>
                      <Checkbox size="small" id={id} checked={c.available && picked.has(c.serviceId)} disabled={!c.available}
                        onChange={() => toggle(c.serviceId)} inputProps={{ 'aria-label': c.name }} />
                      <Box component="label" htmlFor={id} sx={{ flex: 1, minWidth: 0, cursor: c.available ? 'pointer' : 'default' }}>
                        <Typography component="span" sx={{ fontSize: '0.8125rem' }}>{c.name}</Typography>
                        {!c.required && c.available && <Chip label="optional" size="small" sx={{ ml: 1, height: 18, fontSize: '0.6875rem' }} />}
                        {c.notes && <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }} noWrap>{c.notes}</Typography>}
                      </Box>
                      <Typography sx={{ fontSize: '0.8125rem', color: c.available ? 'text.primary' : 'text.secondary', whiteSpace: 'nowrap', pr: 1 }}>
                        {c.available ? aed(priceOf(c.service)) : 'No price for this client'}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, px: 1.5, py: 1, bgcolor: 'action.hover' }}>
                <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                  {`${chosen.length} of ${choices.length} selected · `}
                  <Box component="span" sx={{ fontWeight: 700, color: 'secondary.main' }}>{aed(groupTotal)}</Box>
                </Typography>
                <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={addGroup} disabled={chosen.length === 0}>
                  {`Add ${chosen.length} ${chosen.length === 1 ? 'service' : 'services'}`}
                </Button>
              </Box>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default ServicePicker;
